import React from "react";
import { useTranslation } from "react-i18next";
import { ChartLine } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  AnalyticsAreaFillDefs,
  analyticsAreaFillUrl,
  useAnalyticsChartInstanceId,
} from "../shared/chartAreaFill";
import { formatAnalyticsDayLabel } from "../shared/analyticsFormatting";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { RevenueChartLegend } from "../revenue/RevenueChartLegend";
import { REVENUE_STACKED_AREA_CHART_HEIGHT } from "../revenue/constants";
import { SERVICES_CHART_CARD_ICON_COLOR } from "../services/constants";
import type { OperationsChartDataPoint, OperationsChartSeries } from "./types";

type SeriesDefinition = OperationsChartSeries;

interface OperationsStackedAreaChartProps {
  title: string;
  data: OperationsChartDataPoint[];
  series: SeriesDefinition[];
  stacked?: boolean;
  showLegend?: boolean;
  chartHeight?: number;
  yAxisMax?: number;
  yAxisTicks?: number[];
  yAxisLabel?: string;
  xAxisLabel?: string;
  formatValue?: (value: number) => string;
  formatYAxisTick?: (value: number) => string;
  showTooltipTotal?: boolean;
  embedded?: boolean;
  fillHeight?: boolean;
}

function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

function StackedOperationsTooltip({
  active,
  payload,
  label,
  series,
  formatValue = formatCount,
  showTotal = true,
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string; value?: number }>;
  label?: string;
  series: SeriesDefinition[];
  formatValue?: (value: number) => string;
  showTotal?: boolean;
}) {
  const { t } = useTranslation();

  if (!active || !payload?.length) {
    return null;
  }

  const total = payload.reduce((sum, item) => sum + Number(item.value ?? 0), 0);
  const seriesByKey = Object.fromEntries(
    series.map((item) => [item.key, item]),
  );

  return (
    <div className="border-border/50 bg-background grid min-w-[10rem] gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
      <p className="font-medium text-zinc-900">
        {formatAnalyticsDayLabel(label)}
      </p>
      {payload.map((item) => {
        const config = seriesByKey[String(item.dataKey)];
        if (!config) {
          return null;
        }

        return (
          <div
            key={config.key}
            className="flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: config.color }}
              />
              <span className="text-muted-foreground">{config.label}</span>
            </div>
            <span className="font-medium text-zinc-900">
              {formatValue(Number(item.value ?? 0))}
            </span>
          </div>
        );
      })}
      {showTotal ? (
        <div className="border-border/50 flex items-center justify-between border-t pt-1.5 font-medium text-zinc-900">
          <span>{t("analytics.common.total", "Total")}</span>
          <span>{formatValue(total)}</span>
        </div>
      ) : null}
    </div>
  );
}

export const OperationsStackedAreaChart: React.FC<
  OperationsStackedAreaChartProps
> = ({
  title,
  data,
  series,
  stacked = true,
  showLegend = false,
  chartHeight = REVENUE_STACKED_AREA_CHART_HEIGHT,
  yAxisMax: yAxisMaxProp,
  yAxisTicks: yAxisTicksProp,
  yAxisLabel,
  xAxisLabel,
  formatValue,
  formatYAxisTick,
  showTooltipTotal = true,
  embedded = false,
  fillHeight = false,
}) => {
  const chartConfig = series.reduce<ChartConfig>((config, item) => {
    config[item.key] = {
      label: item.label,
      color: item.color,
    };
    return config;
  }, {});

  const maxValue = data.reduce((max, point) => {
    const pointTotal = series.reduce(
      (sum, item) => sum + Number(point[item.key] ?? 0),
      0,
    );
    return Math.max(max, pointTotal);
  }, 0);
  const computedYAxisMax = Math.ceil(maxValue / 5) * 5 || 20;
  const yAxisMax = yAxisMaxProp ?? computedYAxisMax;
  const valueFormatter = formatValue ?? formatCount;
  const yAxisTickFormatter = formatYAxisTick ?? valueFormatter;
  const yAxisTicks =
    yAxisTicksProp ??
    (yAxisMaxProp !== undefined
      ? Array.from({ length: yAxisMax / 10 + 1 }, (_, index) => index * 10)
      : undefined);
  const chartInstanceId = useAnalyticsChartInstanceId();

  const chartBody = (
    <div
      className={
        fillHeight
          ? "flex min-h-0 flex-1 flex-col gap-3"
          : "flex flex-col gap-3"
      }
    >
      {yAxisLabel ? (
        <p className="shrink-0 text-xs leading-normal font-medium text-[#737373]">
          {yAxisLabel}
        </p>
      ) : null}
      <ChartContainer
        config={chartConfig}
        className={
          fillHeight
            ? "!aspect-auto min-h-0 w-full flex-1"
            : "!aspect-auto w-full"
        }
        style={fillHeight ? undefined : { height: chartHeight }}
      >
        <AreaChart
          data={data}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
        >
          <defs>
            <AnalyticsAreaFillDefs
              seriesKeys={series.map((item) => item.key)}
              chartInstanceId={chartInstanceId}
            />
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="day"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            interval={4}
            className="text-muted-foreground text-xs"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={40}
            domain={[0, yAxisMax]}
            ticks={yAxisTicks}
            tickFormatter={(value) => yAxisTickFormatter(Number(value))}
            className="text-muted-foreground text-xs"
          />
          <ChartTooltip
            cursor={false}
            content={
              <StackedOperationsTooltip
                series={series}
                formatValue={valueFormatter}
                showTotal={showTooltipTotal}
              />
            }
          />
          {series.map((item) => (
            <Area
              key={item.key}
              type="monotone"
              dataKey={item.key}
              stackId={stacked ? "operations" : undefined}
              stroke={`var(--color-${item.key})`}
              fill={analyticsAreaFillUrl(item.key, chartInstanceId)}
              strokeWidth={1.5}
            />
          ))}
        </AreaChart>
      </ChartContainer>
      {xAxisLabel ? (
        <p className="shrink-0 text-xs leading-normal font-medium text-[#737373]">
          {xAxisLabel}
        </p>
      ) : null}
      {showLegend ? (
        <div className="shrink-0">
          <RevenueChartLegend items={series} />
        </div>
      ) : null}
    </div>
  );

  if (embedded) {
    return chartBody;
  }

  return (
    <RevenueChartCard
      title={title}
      icon={<ChartLine />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      width="full"
    >
      {chartBody}
    </RevenueChartCard>
  );
};
